// @ts-nocheck
import React from 'react';
import classNames from 'classnames';
import {
  LuWifi,
  LuWifiOff,
  LuActivity,
  LuCpu,
  LuHardDrive,
  LuPackage,
  LuClock,
  LuShieldCheck,
  LuXCircle,
  LuCheckCircle2,
  LuAlertTriangle,
  LuDatabase,
  LuSmartphone,
} from 'react-icons/lu';
import { FaAndroid } from 'react-icons/fa';

import {
  SMART_COOLER_KPI_HEX,
  SMART_COOLER_KPI_CLASSES,
  getStorageColorTier,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { APP_BORDER, APP_HEX } from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { Text } from '@/components/atoms/AppText';
import { KpiCard } from '@/components/organism/Cards/KpiCard';
import { ISmartCooler } from '../smartCooler.interface';

interface Props {
  item: ISmartCooler;
  isConnected: boolean;
  isOnline: boolean;
  storageUsed: number | null;
  sysInfo: Record<string, any>;
}

export const ViewSCKpiStrip: React.FC<Props> = ({
  item,
  isConnected,
  isOnline,
  storageUsed,
  sysInfo,
}) => {
  return (
    <div
      className={classNames(
        'grid grid-cols-2 lg:grid-cols-4 divide-x',
        APP_BORDER.divideSoft,
        'border-b',
        APP_BORDER.filterRow,
      )}
    >
      {/* Card 1 — Connection */}
      <KpiCard
        label={VIEW_SC_STRINGS.KPI_CONNECTION_LABEL}
        icon={isConnected ? <LuWifi /> : <LuWifiOff />}
        gradientFrom={
          isConnected
            ? SMART_COOLER_KPI_HEX.connected.from
            : SMART_COOLER_KPI_HEX.disconnected.from
        }
        gradientTo={
          isConnected
            ? SMART_COOLER_KPI_HEX.connected.to
            : SMART_COOLER_KPI_HEX.disconnected.to
        }
        glowColor={
          isConnected
            ? SMART_COOLER_KPI_HEX.connected.glow
            : SMART_COOLER_KPI_HEX.disconnected.glow
        }
        statusDot={isConnected ? 'online' : 'offline'}
        primary={
          isConnected
            ? VIEW_SC_STRINGS.KPI_CONN_PRIMARY_LIVE
            : VIEW_SC_STRINGS.KPI_CONN_PRIMARY_OFFLINE
        }
        primaryColorToken={isConnected ? 'success' : 'danger'}
        secondary={
          isConnected
            ? (item.lastConnectedDtm ? getDateTime(item.lastConnectedDtm) : undefined)
            : (item.lastDisconnectedDtm ? getDateTime(item.lastDisconnectedDtm) : undefined)
        }
        sub={
          isConnected
            ? (item.lastConnectedDtm
              ? getDateTimeAgo(item.lastConnectedDtm)
              : VIEW_SC_STRINGS.KPI_CONN_SUB_NEVER)
            : (item.lastDisconnectedDtm
              ? getDateTimeAgo(item.lastDisconnectedDtm)
              : VIEW_SC_STRINGS.KPI_CONN_SUB_DISC_NEVER)
        }
        subIcon={<LuClock />}
        footerRight={
          <span
            className={classNames(
              'text-[10px] font-semibold uppercase tracking-wider rounded-md px-1.5 py-0.5',
              isConnected
                ? SMART_COOLER_KPI_CLASSES.connected.footerChip
                : SMART_COOLER_KPI_CLASSES.disconnected.footerChip,
            )}
          >
            {isConnected
              ? SMART_COOLER_KPI_CLASSES.connected.footerLabel
              : SMART_COOLER_KPI_CLASSES.disconnected.footerLabel}
          </span>
        }
      />

      {/* Card 2 — System State */}
      <KpiCard
        label={VIEW_SC_STRINGS.KPI_SYS_STATE_LABEL}
        icon={<LuActivity />}
        gradientFrom={
          isOnline
            ? SMART_COOLER_KPI_HEX.sysOnline.from
            : SMART_COOLER_KPI_HEX.sysOffline.from
        }
        gradientTo={
          isOnline
            ? SMART_COOLER_KPI_HEX.sysOnline.to
            : SMART_COOLER_KPI_HEX.sysOffline.to
        }
        glowColor={
          isOnline
            ? SMART_COOLER_KPI_HEX.sysOnline.glow
            : SMART_COOLER_KPI_HEX.sysOffline.glow
        }
        statusDot={isOnline ? 'online' : 'warning'}
        primary={
          isOnline
            ? VIEW_SC_STRINGS.KPI_SYS_PRIMARY_ONLINE
            : VIEW_SC_STRINGS.KPI_SYS_PRIMARY_OFFLINE
        }
        primaryColorToken={isOnline ? 'success' : 'warning'}
        secondary={
          item.lastHeartbeatDtm ? getDateTime(item.lastHeartbeatDtm) : undefined
        }
        sub={
          item.lastHeartbeatDtm
            ? `${VIEW_SC_STRINGS.KPI_SYS_SUB_HEARTBEAT} ${getDateTimeAgo(item.lastHeartbeatDtm)}`
            : VIEW_SC_STRINGS.KPI_SYS_SUB_NONE
        }
        subIcon={<LuCpu />}
        footerRight={
          <span
            className={classNames(
              'text-[10px] font-semibold uppercase tracking-wider rounded-md px-1.5 py-0.5',
              isOnline
                ? SMART_COOLER_KPI_CLASSES.sysOnline.footerChip
                : SMART_COOLER_KPI_CLASSES.sysOffline.footerChip,
            )}
          >
            {isOnline
              ? SMART_COOLER_KPI_CLASSES.sysOnline.footerLabel
              : SMART_COOLER_KPI_CLASSES.sysOffline.footerLabel}
          </span>
        }
      />

      {/* Card 3 — App Version */}
      <KpiCard
        label={VIEW_SC_STRINGS.KPI_APP_VERSION_LABEL}
        icon={<LuPackage />}
        gradientFrom={SMART_COOLER_KPI_HEX.appVersion.from}
        gradientTo={SMART_COOLER_KPI_HEX.appVersion.to}
        glowColor={SMART_COOLER_KPI_HEX.appVersion.glow}
        primary={sysInfo.appVersion ?? '—'}
        primaryColor={SMART_COOLER_KPI_CLASSES.appVersion.primaryText}
        secondary={
          <span className="inline-flex items-center gap-1">
            <FaAndroid
              className="w-3 h-3"
              style={{ color: APP_HEX.androidGreen }}
            />
            {sysInfo.osVersion
              ? `${VIEW_SC_STRINGS.KPI_VERSION_OS_PREFIX} ${sysInfo.osVersion}`
              : VIEW_SC_STRINGS.KPI_VERSION_OS_FALLBACK}
          </span>
        }
        sub={item.nativeId || '—'}
        subIcon={<LuSmartphone />}
        footerRight={
          sysInfo.appVersion ? (
            <span
              className={classNames(
                'text-[10px] font-semibold uppercase tracking-wider rounded-md px-1.5 py-0.5',
                SMART_COOLER_KPI_CLASSES.appVersion.footerChip,
              )}
            >
              {VIEW_SC_STRINGS.KPI_VERSION_INSTALLED}
            </span>
          ) : undefined
        }
      />

      {/* Card 4 — Storage */}
      {(() => {
        const sTier = getStorageColorTier(storageUsed);
        const freeMb = sysInfo.availableStorageMb;
        const totalMb = sysInfo.totalStorageMb;
        return (
          <KpiCard
            label={VIEW_SC_STRINGS.KPI_STORAGE_LABEL}
            icon={<LuHardDrive />}
            gradientFrom={sTier.from}
            gradientTo={sTier.to}
            glowColor={sTier.from}
            primary={storageUsed !== null ? `${storageUsed}%` : '—'}
            primaryColor={sTier.text}
            secondary={
              freeMb && totalMb
                ? `${freeMb} ${VIEW_SC_STRINGS.SYS_MB_SUFFIX} free of ${totalMb} ${VIEW_SC_STRINGS.SYS_MB_SUFFIX}`
                : VIEW_SC_STRINGS.KPI_STORAGE_USAGE_UNKNOWN
            }
            extra={
              storageUsed !== null ? (
                <div className="space-y-1">
                  <div
                    className={classNames(
                      'h-2 w-full rounded-full overflow-hidden',
                      'track' in sTier ? sTier.track : '',
                    )}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${storageUsed}%`,
                        background: `linear-gradient(to right, ${sTier.from}, ${sTier.to})`,
                        boxShadow: `0 0 6px ${sTier.from}60`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between">
                    <Text as="span" size="xs" color="subtle">
                      0%
                    </Text>
                    <Text as="span" size="xs" color="subtle">
                      100%
                    </Text>
                  </div>
                </div>
              ) : undefined
            }
            sub={
              storageUsed !== null
                ? 'sub' in sTier
                  ? sTier.sub
                  : ''
                : VIEW_SC_STRINGS.KPI_STORAGE_NO_DATA
            }
            subIcon={<LuDatabase />}
            footerRight={
              storageUsed !== null && 'chip' in sTier ? (
                <span
                  className={classNames(
                    'text-[10px] font-semibold uppercase tracking-wider rounded-md px-1.5 py-0.5',
                    sTier.chip,
                  )}
                >
                  {'label' in sTier ? sTier.label : ''}
                </span>
              ) : undefined
            }
          />
        );
      })()}
    </div>
  );
};
